import React, { useEffect, useState, useCallback } from 'react';
import { Link, NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Permissions } from '../../config';
import useSWR, { mutate } from 'swr';
import debounce from 'lodash/debounce';
import { useInView } from 'react-intersection-observer';

// material-ui
import { createUseStyles } from 'react-jss';
import {
    Assignment,
    Edit,
    AddAlert,
    ListAlt,
    Add,
    ViewList,
    ViewModule,
    BarChart,
} from '@mui/icons-material';
import { Box } from '@mui/material';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import DeleteButton from 'components/Main/DeleteButton.js';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog.js';
import SparaFilter from 'components/SparaFilter/SparaFilter';

// Custom Logo Icon Component for SparaFilter
const LogoIcon = ({ src, alt }) => {
    return (
        <img
            src={src}
            alt={alt}
            style={{
                width: '16px',
                height: '16px',
                marginRight: '4px',
                borderRadius: '2px',
            }}
        />
    );
};

import styles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';

import OfferTable from './offerTable';
import OfferCardView from './OfferCardView';
import NotificationModal from './NotificationModal/NotificationModal';
import NotificationProgressModal from './NotificationProgressModal';

import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import { offerApi, groupApi, fetcher } from '../../lib/api';

const useStyles = createUseStyles({
    ...styles,
    mobileGridItem: {
        '@media (max-width: 600px)': {
            padding: '0 4px !important',
        },
    },
});

const DIALOG_CLOSED = {
    open: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    onCancel: null,
    confirmText: undefined,
    cancelText: undefined,
};

const shimmerBase = {
    background:
        'linear-gradient(90deg, rgba(222,44,72,0.06) 25%, rgba(222,44,72,0.14) 50%, rgba(222,44,72,0.06) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer-offers 1.4s ease-in-out infinite',
    borderRadius: '8px',
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('shimmer-offers-style')) {
    const style = document.createElement('style');
    style.id = 'shimmer-offers-style';
    style.textContent =
        '@keyframes shimmer-offers { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }';
    document.head.appendChild(style);
}

const OfferCardSkeleton = () => (
    <div
        style={{
            width: '100%',
            borderRadius: '16px',
            border: '1px solid #f0e0e3',
            overflow: 'hidden',
            backgroundColor: 'var(--spara-surface, #fff)',
        }}
    >
        <div style={{ margin: '8px' }}>
            <div style={{ ...shimmerBase, height: 160, borderRadius: '10px' }} />
        </div>
        <div style={{ padding: '8px 12px 12px' }}>
            <div style={{ ...shimmerBase, height: 14, width: '40%', marginBottom: 8 }} />
            <div style={{ ...shimmerBase, height: 18, width: '80%', marginBottom: 12 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ ...shimmerBase, width: 24, height: 24, borderRadius: '50%' }} />
                <div style={{ ...shimmerBase, height: 12, width: '50%' }} />
            </div>
        </div>
    </div>
);

const OfferTableRowSkeleton = () => (
    <div
        style={{
            display: 'flex',
            gap: 16,
            padding: '12px 16px',
            borderBottom: '1px solid #fce8eb',
            alignItems: 'center',
        }}
    >
        <div style={{ ...shimmerBase, width: 48, height: 36, borderRadius: 6, flexShrink: 0 }} />
        <div style={{ flex: 2 }}>
            <div style={{ ...shimmerBase, height: 14, width: '70%' }} />
        </div>
        <div style={{ flex: 1 }}>
            <div style={{ ...shimmerBase, height: 14, width: '60%' }} />
        </div>
        <div style={{ flex: 1 }}>
            <div style={{ ...shimmerBase, height: 14, width: '50%' }} />
        </div>
        <div style={{ flex: 1 }}>
            <div style={{ ...shimmerBase, height: 14, width: '40%' }} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ ...shimmerBase, width: 32, height: 32, borderRadius: 6 }} />
            <div style={{ ...shimmerBase, width: 32, height: 32, borderRadius: 6 }} />
        </div>
    </div>
);

const Offers = ({ t }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [notification, setNotification] = useState({ status: false, type: '', message: '' });
    const [dialog, setDialog] = useState(DIALOG_CLOSED);
    const userStore = JSON.parse(localStorage.getItem('user'));
    const userPermission = userStore?.permission || [];
    const userRoles = Array.isArray(userStore?.roles)
        ? userStore.roles
        : userStore?.roles
          ? Object.values(userStore.roles)
          : [];
    const isSuperAdmin = userRoles.includes('super_admin');
    const canNotify =
        isSuperAdmin ||
        userPermission.includes(Permissions.OFFER_NOTIFY) ||
        userPermission.includes('offer-notify') ||
        userRoles.includes('company_user') ||
        userRoles.includes('group_admin') ||
        userRoles.includes('manager');
    const canUpdate =
        userPermission.includes(Permissions.OFFER_UPDATE) ||
        userPermission.includes(Permissions.CU_OFFER_UPDATE);
    const canDelete =
        userPermission.includes(Permissions.OFFER_DELETE) ||
        userPermission.includes(Permissions.CU_OFFER_DELETE);
    const canCreate =
        userPermission.includes(Permissions.OFFER_CREATE) ||
        userPermission.includes(Permissions.CU_OFFER_CREATE);
    const canViewDetails =
        userPermission.includes(Permissions.OFFER_DETAILS) ||
        userPermission.includes(Permissions.CU_OFFER_VIEW);

    const isCompanyUserOrGroupAdmin =
        userRoles.includes('company_user') || userRoles.includes('group_admin');

    const userCompanyIds = (userStore?.companyIds || [])
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map(Number);
    const userGroupIds = (userStore?.groupIds || [])
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map(Number);

    const canManageOffer = (offer) => {
        if (!isCompanyUserOrGroupAdmin) return true;

        if (userCompanyIds.includes(Number(offer.torgid_company_id))) return true;

        if (offer.group_ids) {
            const offerGroupIds = String(offer.group_ids)
                .split(',')
                .map((x) => Number(x))
                .filter((n) => !Number.isNaN(n) && n > 0);
            if (
                offerGroupIds.length > 0 &&
                offerGroupIds.every((gid) => userGroupIds.includes(gid))
            ) {
                return true;
            }
        }

        return false;
    };

    const canNotifyOffer = (offer) => {
        if (isSuperAdmin || userRoles.includes('manager')) return true;

        if (userCompanyIds.includes(Number(offer.torgid_company_id))) return true;

        if (offer.group_ids) {
            const offerGroupIds = String(offer.group_ids)
                .split(',')
                .map((x) => Number(x))
                .filter((n) => !Number.isNaN(n) && n > 0);
            return offerGroupIds.some((gid) => userGroupIds.includes(gid));
        }

        return false;
    };
    const { data: specialCompaniesData, isLoading: loadingGroup } = useSWR(groupApi.list(), fetcher);
    const specialCompanies = specialCompaniesData ?? {};
    const [notificationModal, setNotificationModal] = useState({ open: false, offer: null });
    const [notificationDisabledDialog, setNotificationDisabledDialog] = useState({ open: false, message: '' });
    const [notificationBatchIds, setNotificationBatchIds] = useState([]);
    const [notificationTotalCount, setNotificationTotalCount] = useState(0); // Bætt við
    const [notificationProgress, setNotificationProgress] = useState({
        open: false,
        progress: 0,
        sentCount: 0,
        totalCount: 0,
    });

    // Valinn hópur (nafn) fyrir prenthaus - verður reiknað eftir að filters hefur verið skilgreint

    // State til að halda utan um offer sem er að breytast
    const [changingOffer, setChangingOffer] = useState(null);

    // Setup intersection observer for infinite scroll
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        rootMargin: '100px',
    });

    // URL params for filters
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Filter state - initialize from URL params
    const [filters, setFilters] = useState(() => {
        const urlFilters = {};
        const status = searchParams.get('status');
        const active = searchParams.get('active');
        const display = searchParams.get('display');
        const group = searchParams.get('group');
        const category = searchParams.get('category');

        if (status) urlFilters.status = status;
        if (active) urlFilters.active = active;
        if (display) urlFilters.display = display;
        if (group) urlFilters.group = group;
        if (category) urlFilters.category = category;

        return urlFilters;
    });
    const [showStats, setShowStats] = useState(searchParams.get('sort') === 'activity');
    const sort = showStats ? 'activity' : null;
    const [activityDays, setActivityDays] = useState(() => {
        const d = searchParams.get('days');
        return d ? parseInt(d, 10) : 30;
    });
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

    // Infinite loading state
    const [page, setPage] = useState(1);
    const [pageLimit] = useState(50);
    const [isActiveLoadMore, setIsActiveLoadMore] = useState(true);
    const [allOffers, setAllOffers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Debug allOffers state changes
    React.useEffect(() => {
        console.log('allOffers state changed:', {
            length: allOffers.length,
            firstItem: allOffers[0],
            lastItem: allOffers[allOffers.length - 1],
        });
    }, [allOffers]);

    // View state
    const [viewMode, setViewMode] = useState(() => {
        const saved = localStorage.getItem('offersViewMode');
        return saved || 'card';
    }); // 'table' or 'card'
    // Card pagination - ekki notað fyrir infinite loading
    const [cardPageIndex, setCardPageIndex] = useState(0);
    const [cardPageSize, setCardPageSize] = useState(50);

    // Debug card pagination changes
    React.useEffect(() => {
        console.log('Card pagination changed:', {
            cardPageIndex,
            cardPageSize,
            allOffersLength: allOffers.length,
        });
    }, [cardPageIndex, cardPageSize, allOffers.length]);

    // Valinn hópur (nafn) fyrir prenthaus - reikna Eftir filters skilgreiningu
    const selectedGroupName = React.useMemo(() => {
        // Ef notandi valdi "special" (heiti beint), notum það
        if (filters?.special) {
            return filters.special;
        }
        // Annars notum við group (ID) og flettum upp í specialCompanies
        const groupValue = filters?.group;
        if (!groupValue) return null;
        if (groupValue === 'null') return 'Almennir notendur';
        const groupId = Number(groupValue);
        const spec = specialCompanies?.results?.find((s) => Number(s.id) === groupId);
        return spec?.name || null;
    }, [filters?.group, filters?.special, specialCompanies?.results]);

    // Custom fetcher fyrir marga batcha með nýja endpoint
    const batchFetcher = async (...args) => {
        console.log('batchFetcher called with args:', args);

        // useSWR sendir url sem fyrsta parameter og önnur sem args
        let batchIds;
        if (Array.isArray(args[0])) {
            // Ef fyrsti parameter er array, þá er batchIds í args0
            batchIds = args[0][1];
        } else {
            // Ef fyrsti parameter er string, þá er batchIds í args[1]
            batchIds = args[1];
        }

        console.log('Extracted batchIds:', batchIds);
        if (!batchIds || batchIds.length === 0) {
            console.log('No batchIds, returning empty array');
            return [];
        }

        try {
            console.log('Making POST request to /offer/notification/batch/multiple with:', {
                batch_ids: batchIds,
            });
            const response = await fetcher('/offer/notification/batch/multiple', 'POST', {
                batch_ids: batchIds,
            });

            console.log('Response from batch/multiple:', response);
            // Skila batches array úr svari
            return response?.results?.batches || [];
        } catch (error) {
            console.error('Error fetching batch status:', error);
            return [];
        }
    };

    console.log('notificationBatchIds:', notificationBatchIds);
    const { data: notificationBatchesData } = useSWR(
        notificationBatchIds.length > 0
            ? ['notification-batch-progress', notificationBatchIds]
            : null,
        batchFetcher,
        {
            refreshInterval: 2000,
            dedupingInterval: 1500,
        }
    );
    console.log('notificationBatchesData:', notificationBatchesData);

    // Greina hvort notandi er company user (ekki admin)
    const store = JSON.parse(localStorage.getItem('user'));
    const isCompanyUser = !userPermission.includes(Permissions.OFFER_VIEW);
    // Filter out null/undefined values before joining
    const companyIds =
        store.companyIds?.filter((id) => id !== null && id !== undefined).length > 0
            ? store.companyIds.filter((id) => id !== null && id !== undefined).join(',')
            : null;
    const groupIds =
        store.groupIds?.filter((id) => id !== null && id !== undefined).length > 0
            ? store.groupIds.filter((id) => id !== null && id !== undefined).join(',')
            : null;
    const userIsGroupAdmin = store && store.roles.length === 1 && store.roles[0] === 'group_admin';
    const urlCompanyId = searchParams.get('company_id');

    // Búa til SWR key sem breytist eftir filterum og pagination
    const swrKey = offerApi.list({
        page,
        limit: pageLimit,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filters.display && { display: filters.display }),
        ...(filters.group && { group: filters.group }),
        ...(filters.category && { category: filters.category }),
        ...(sort && { sort }),
        ...(sort === 'activity' && activityDays && { days: activityDays }),
        ...(urlCompanyId
            ? { company_ids: urlCompanyId }
            : isCompanyUser && !userIsGroupAdmin && companyIds && { company_ids: companyIds }),
        ...(!urlCompanyId && isCompanyUser && groupIds && { group_ids: groupIds }),
    });

    console.log('SWR Key:', swrKey, {
        page,
        debouncedSearch,
        filters,
    });

    // SWR hook fyrir offers með filterum
    const { data: offersData, error: offersError, mutate: mutateOffers, isValidating } = useSWR(
        swrKey,
        fetcher,
        {
            refreshInterval: 0,
            revalidateOnFocus: false,
            keepPreviousData: true,
        }
    );

    const { data: hasGeneralData } = useSWR(
        isCompanyUserOrGroupAdmin ? offerApi.hasGeneral() : null,
        fetcher
    );
    const hasGeneralOffers = hasGeneralData?.results?.has_general_offers ?? false;

    // Skilgreina offers frá SWR data (ekki notað fyrir infinite loading)
    const offers = offersData?.results?.data || [];

    // Infinite loading logic
    const shouldLoadMore = useCallback(() => {
        console.log('shouldLoadMore called:', {
            isValidating,
            allOffersLength: allOffers.length,
            totalCount,
            isActiveLoadMore,
            page,
            condition1: !isValidating,
            condition2: allOffers.length > 0,
            condition3: allOffers.length < totalCount,
            condition4: isActiveLoadMore,
        });

        if (
            !isValidating &&
            allOffers.length > 0 &&
            allOffers.length < totalCount &&
            isActiveLoadMore
        ) {
            console.log('Loading more, current page:', page);
            setPage((prevPage) => {
                const nextPage = prevPage + 1;
                console.log('Setting page from', prevPage, 'to', nextPage);
                return nextPage;
            });
        } else {
            console.log('Not loading more - conditions not met');
        }
    }, [isValidating, allOffers.length, totalCount, isActiveLoadMore]);

    // Handle scroll events as backup
    React.useEffect(() => {
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const scrollHeight = document.documentElement.scrollHeight;
                const scrollTop = window.scrollY;
                const clientHeight = window.innerHeight;
                const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

                // If we're close to bottom (within 200px), try to load more
                if (distanceFromBottom < 200) {
                    console.log('Scroll handler - distance from bottom:', distanceFromBottom);
                    shouldLoadMore();
                }
            }, 100);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [shouldLoadMore]);

    // Handle intersection observer
    React.useEffect(() => {
        console.log('Intersection observer inView:', inView);
        if (inView) {
            // Debounce shouldLoadMore to prevent endless loop
            const timeoutId = setTimeout(() => {
                shouldLoadMore();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [inView, shouldLoadMore]);

    // Update local state when new data arrives
    React.useEffect(() => {
        console.log('Data update effect:', {
            hasData: !!offersData?.results,
            page,
            dataLength: offersData?.results?.data?.length,
            totalCount: offersData?.results?.pagination?.total,
            fullData: offersData,
        });

        if (offersData?.results) {
            // API skilar results wrapper með data og pagination
            const data = offersData.results.data;
            const count = offersData.results.pagination?.total || 0;

            console.log('Processing data:', {
                dataLength: data?.length,
                isArray: Array.isArray(data),
                firstItem: data?.[0],
                count,
            });

            if (!Array.isArray(data)) {
                console.error('Data is not an array:', data);
                return;
            }

            if (page === 1) {
                // Reset data when filters change
                console.log('Resetting data for page 1, data length:', data.length);
                setAllOffers(data);
                console.log('setAllOffers called with data length:', data.length);
            } else {
                // Append new data for infinite scroll
                console.log('Appending data for page', page, 'data length:', data.length);
                setAllOffers((prev) => {
                    console.log('Previous offers length:', prev.length);
                    // Forðumst að bæta við sömu gögnum aftur
                    const newIds = new Set(data.map((offer) => offer.id));
                    const filteredPrev = prev.filter((offer) => !newIds.has(offer.id));
                    console.log('Filtered prev length:', filteredPrev.length);
                    const newData = [...filteredPrev, ...data];
                    console.log('New data length:', newData.length, 'added:', data.length);
                    console.log('setAllOffers called with new data length:', newData.length);
                    return newData;
                });
            }
            setTotalCount(count);

            // Disable infinite scroll if we've loaded all items
            if (data.length < pageLimit) {
                console.log('Disabling infinite scroll - loaded all items');
                setIsActiveLoadMore(false);
            }
        }
    }, [offersData, page, pageLimit]);

    // Scroll to top when filters/search cause page 1 data to load
    useEffect(() => {
        if (page === 1) {
            // Use setTimeout to ensure scroll happens after potential re-renders
            setTimeout(() => {
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0; // For older browsers/compatibility
            }, 0);
        }
    }, [allOffers, page]); // Trigger when data changes AND page is 1

    // useEffect fyrir notificationBatchData skal líka vera efst
    useEffect(() => {
        if (!notificationBatchesData || notificationBatchesData.length === 0) return;

        let completedSum = 0;
        let totalSum = 0;
        let allFinished = true;

        notificationBatchesData.forEach((batch) => {
            const total = batch?.total_jobs || 0;
            const pending = batch?.pending_jobs || 0;
            const finished = batch?.status === 'finished' || pending === 0;

            completedSum += total - pending;
            totalSum += total;

            if (!finished) allFinished = false;
        });

        const percent = totalSum > 0 ? Math.round((completedSum / totalSum) * 100) : 0;

        setNotificationProgress((prev) => ({
            ...prev,
            progress: percent,
            sentCount: completedSum,
            totalCount: totalSum,
        }));

        setNotificationTotalCount(totalSum);

        if (allFinished) {
            setNotificationBatchIds([]);
            setNotificationProgress({
                open: false,
                progress: 100,
                sentCount: 0,
                totalCount: totalSum,
            });
            // Uppfæra SWR cache eftir notification
            mutateOffers();
            setDialog({
                open: true,
                title: t('notification_sent'),
                message: t('notification_was_sent', { count: totalSum }),
                type: 'success',
                onConfirm: () => setDialog(DIALOG_CLOSED),
                onCancel: null,
                confirmText: t('ok'),
            });
        }
    }, [notificationBatchesData, t]);

    // Reset card page when filters change (ekki þarf fyrir infinite loading)
    useEffect(() => {
        setCardPageIndex(0);
    }, [filters, search]);

    const updateButton = (offer) => {
        return (
            <Button
                color="success"
                className={classes.actionButton}
                title={t('edit')}
                style={{ margin: 0 }}
                component="a"
                href={`/offers/${offer.id}`}
                onClick={(e) => {
                    if (!e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                        navigate(`/offers/${offer.id}`);
                    }
                }}
            >
                <Edit className={classes.icon} />
            </Button>
        );
    };

    const reportButton = (offer) => {
        return (
            <Button
                color="warning"
                key={offer.id + '_report'}
                style={{ margin: 0 }}
                className={classes.actionButton}
                title={t('statistic')}
                component="a"
                href={`/offers/${offer.id}/statistics`}
                onClick={(e) => {
                    if (!e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                        navigate(`/offers/${offer.id}/statistics`);
                    }
                }}
            >
                <ListAlt className={classes.icon} />
            </Button>
        );
    };
    const deleteButton = (offer) => {
        return (
            <DeleteButton
                key={offer.id + '_delete'}
                style={{ margin: 0 }}
                onClick={() => {
                    setDialog({
                        open: true,
                        title: t('offer.delete_title'),
                        message: t('offer.delete_text'),
                        type: 'danger',
                        onConfirm: async () => {
                            try {
                                await fetcher(offerApi.delete(offer.id), 'DELETE');
                                mutateOffers();
                            } catch (error) {
                                setNotification({ status: true, type: 'error', message: error?.response?.data?.message || 'Villa við að eyða tilboði' });
                            }
                            setDialog(DIALOG_CLOSED);
                        },
                        onCancel: () => setDialog(DIALOG_CLOSED),
                        confirmText: t('delete_yes'),
                        cancelText: t('cancel'),
                    });
                }}
            />
        );
    };

    const changeOfferActive = (offer) => {
        setDialog({
            open: true,
            title: offer.is_active
                ? t('offer.change_status_deactive')
                : t('offer.change_status_active'),
            message: offer.is_active
                ? t('offer.change_status_deactive_text')
                : t('offer.change_status_active_text'),
            type: 'warning',
            onConfirm: async () => {
                setDialog(DIALOG_CLOSED);
                await successChange(offer);
            },
            onCancel: () => setDialog(DIALOG_CLOSED),
            confirmText: t('yes'),
            cancelText: t('cancel'),
        });
    };

    const duplicateOffer = (offer) => {
        navigate(`/offers/new?duplicateFrom=${offer.id}&step=1`);
    };

    const successChange = async (offer) => {
        try {
            setChangingOffer(offer);

            // Kalla á API beint með SWR mutate
            const response = await fetcher(
                `/offer/activate/${offer.id}/${!offer.is_active}`,
                'GET'
            );

            if (response) {
                // Uppfæra SWR cache með nýju gögnum
                await mutateOffers();

                // Uppfæra URL params eftir að SWR cache hefur verið uppfært
                const newSearchParams = new URLSearchParams(searchParams);
                const currentActiveFilter = searchParams.get('active');
                const newActiveStatus = !offer.is_active; // Nýja status

                // Ef offer er deactivated og við erum með active=true filter, hreinsa filter
                if (!newActiveStatus && currentActiveFilter === 'true') {
                    newSearchParams.delete('active');
                    setSearchParams(newSearchParams);
                }
                // Ef offer er activated og við erum með active=false filter, hreinsa filter
                else if (newActiveStatus && currentActiveFilter === 'false') {
                    newSearchParams.delete('active');
                    setSearchParams(newSearchParams);
                }
            }

            setChangingOffer(null);
        } catch (error) {
            console.error('Error changing offer status:', error);
            setChangingOffer(null);
        }
    };

    const notificationButton = (offer) => {
        if (!canNotify || !canNotifyOffer(offer)) return null;

        if (offer.is_active && offer.approved && !offer.notified) {
            return (
                <Button
                    color="primary"
                    className={classes.actionButton}
                    title={t('send_notification')}
                    style={{ margin: 0 }}
                    data-testid={`offer-notify-${offer.id}`}
                    onClick={() => setNotificationModal({ open: true, offer })}
                >
                    <AddAlert className={classes.icon} />
                </Button>
            );
        }

        if (offer.is_active && offer.approved && offer.notified) {
            return (
                <Button
                    color="primary"
                    className={classes.actionButton}
                    title={t('notification.retry_title') || 'Senda á þá sem eftir eru'}
                    style={{ margin: 0, opacity: 0.85 }}
                    data-testid={`offer-notify-retry-${offer.id}`}
                    onClick={() => setNotificationModal({ open: true, offer })}
                >
                    <AddAlert className={classes.icon} />
                </Button>
            );
        }

        const disabledReason = offer.notified
            ? t('notification.already_sent')
            : t('notification.not_visible');
        return (
            <Button
                color="primary"
                className={classes.actionButton}
                title={disabledReason}
                style={{ margin: 0, opacity: 0.4 }}
                data-testid={`offer-notify-disabled-${offer.id}`}
                onClick={() =>
                    setNotificationDisabledDialog({ open: true, message: disabledReason })
                }
            >
                <AddAlert className={classes.icon} />
            </Button>
        );
    };

    // Filter functions
    const handleFilterClick = (filterId, value) => {
        const currentValue = filters[filterId];
        const newValue = currentValue === value ? null : value;

        console.log('Filter click:', {
            filterId,
            value,
            currentValue,
            newValue,
        });

        const newFilters = {
            ...filters,
            [filterId]: newValue,
        };
        setFilters(newFilters);
        setPage(1);
        setIsActiveLoadMore(true);

        // Clear allOffers when filters change
        console.log('Clearing allOffers due to filter change');
        setAllOffers([]);
        setTotalCount(0);

        // Update URL params
        const newSearchParams = new URLSearchParams(searchParams);
        if (newFilters[filterId]) {
            newSearchParams.set(filterId, newFilters[filterId]);
        } else {
            newSearchParams.delete(filterId);
        }
        setSearchParams(newSearchParams);
    };

    const handleClearAll = () => {
        setFilters({});
        setSearch('');
        setDebouncedSearch('');
        setPage(1);
        setIsActiveLoadMore(true);

        // Clear allOffers when clearing all filters
        console.log('Clearing allOffers due to clear all');
        setAllOffers([]);
        setTotalCount(0);

        setSearchParams(new URLSearchParams());
    };

    const handleSearchChange = (e) => {
        const newSearch = e.target.value;
        setSearch(newSearch);
        setPage(1);
        setIsActiveLoadMore(true);

        // Clear allOffers when search changes
        console.log('Clearing allOffers due to search change');
        setAllOffers([]);
        setTotalCount(0);

        // Update URL params
        const newSearchParams = new URLSearchParams(searchParams);
        if (newSearch) {
            newSearchParams.set('search', newSearch);
        } else {
            newSearchParams.delete('search');
        }
        setSearchParams(newSearchParams);
    };

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [search]);

    // Effect fyrir filters - SWR mun sjálfkrafa revalidate þegar swrKey breytist
    useEffect(() => {
        // SWR mun sjálfkrafa revalidate þegar filters breytast
    }, [filters]);

    // Nota allOffers fyrir infinite loading
    const filteredOffers = allOffers;

    console.log('Filtered offers:', {
        allOffersLength: allOffers.length,
        filteredOffersLength: filteredOffers.length,
        page,
        totalCount,
        cardPageIndex,
        cardPageSize,
        allOffersFirstItem: allOffers[0],
        allOffersLastItem: allOffers[allOffers.length - 1],
    });

    // Get all special companies for filter options
    const getSpecialCompanyOptions = () => {
        const options = [];

        if (!userIsGroupAdmin && (!isCompanyUserOrGroupAdmin || hasGeneralOffers)) {
            options.push({
                id: 'group',
                label: 'Almennir notendur',
                value: 'null',
                color: '#666',
                icon: null,
            });
        }

        if (specialCompanies.results) {
            const userGroupIds = userStore.groupIds || [];
            const visibleGroups = isCompanyUserOrGroupAdmin
                ? specialCompanies.results.filter((spec) => userGroupIds.includes(spec.id))
                : specialCompanies.results;

            if (!(userIsGroupAdmin && visibleGroups.length <= 1)) {
                visibleGroups.forEach((spec) => {
                    options.push({
                        id: 'group',
                        label: spec.name,
                        value: spec.id,
                        color: '#078f5c',
                        icon: spec.logo ? () => <LogoIcon src={spec.logo} alt={spec.name} /> : null,
                    });
                });
            }
        }

        // Bæta við torgid_special_company sem eru í special companies listanum
        if (offersData?.results?.data && specialCompanies.results) {
            const uniqueSpecialCompanies = new Set();
            offersData.results.data.forEach((offer) => {
                if (offer.torgid_special_company) {
                    const specialCompanyInList = specialCompanies.results.find(
                        (spec) => spec.id === offer.torgid_special_company.id
                    );
                    if (
                        specialCompanyInList &&
                        !uniqueSpecialCompanies.has(specialCompanyInList.name)
                    ) {
                        uniqueSpecialCompanies.add(specialCompanyInList.name);
                        // Athuga hvort þessi valkostur er þegar til
                        const alreadyExists = options.some(
                            (option) => option.value === specialCompanyInList.name
                        );
                        if (!alreadyExists) {
                            options.push({
                                id: 'special',
                                label: specialCompanyInList.name,
                                value: specialCompanyInList.name,
                                color: '#078f5c',
                                icon: specialCompanyInList.logo
                                    ? () => (
                                          <LogoIcon
                                              src={specialCompanyInList.logo}
                                              alt={specialCompanyInList.name}
                                          />
                                      )
                                    : null,
                            });
                        }
                    }
                }
            });
        }

        return options;
    };

    if (offersError) {
        return (
            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardBody>
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <h4>Villa kom upp við að sækja tilboð</h4>
                                <p>{offersError.message}</p>
                                <Button color="primary" onClick={() => mutateOffers()}>
                                    Reyna aftur
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
        );
    }
    return (
        <GridContainer>
            <ConfirmDialog
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                onConfirm={dialog.onConfirm}
                onCancel={dialog.onCancel}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                testId="offers-confirm-dialog"
            />
            <ConfirmDialog
                open={notificationDisabledDialog.open}
                type="info"
                title={t('notification.disabled_title')}
                message={notificationDisabledDialog.message}
                onConfirm={() => setNotificationDisabledDialog({ open: false, message: '' })}
                confirmText={t('close')}
                testId="notification-disabled-dialog"
            />
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.status}
                closeNotification={() => setNotification({ status: false, type: '', message: '' })}
            />
            <GridItem xs={12} className={classes.mobileGridItem}>
                <SparaTitle
                    title={
                        selectedGroupName
                            ? `Tilboð í hópnum „${selectedGroupName}“`
                            : t('menu.offers')
                    }
                    rightComponent={
                        <>
                            <div className="print-inline-logo" style={{ display: 'none' }}>
                                Spara
                            </div>
                            {(userPermission.includes(Permissions.OFFER_CREATE) ||
                                userPermission.includes(Permissions.CU_OFFER_CREATE)) && (
                                <SparaButton
                                    color="primary"
                                    icon={Add}
                                    data-testid="offers-add-new"
                                    onClick={() => navigate('/offers/new')}
                                >
                                    {t('offer.add_new')}
                                </SparaButton>
                            )}
                        </>
                    }
                />
                {/* print-only-group-title ekki lengur þörf þar sem við breytum SparaTitle */}
                <Card style={{ margin: '0px' }}>
                    <CardBody className="offers-card-body" style={{ padding: '12px 8px' }}>
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '16px',
                                flexWrap: 'wrap',
                                gap: 8,
                            }}
                        >
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <SparaButton
                                    variant={showStats ? 'filled' : 'outlined'}
                                    color="secondary"
                                    size="small"
                                    icon={BarChart}
                                    onClick={() => {
                                        const newShow = !showStats;
                                        setShowStats(newShow);
                                        setPage(1);
                                        setAllOffers([]);
                                        const params = new URLSearchParams(searchParams);
                                        if (newShow) {
                                            params.set('sort', 'activity');
                                            setActivityDays(30);
                                            params.set('days', '30');
                                        } else {
                                            params.delete('sort');
                                            params.delete('days');
                                        }
                                        setSearchParams(params, { replace: true });
                                    }}
                                >
                                    {showStats ? t('dash.hide_statistics') : t('dash.show_statistics')}
                                </SparaButton>
                                {showStats && (
                                    <>
                                        <SparaButton
                                            variant={activityDays === 30 ? 'filled' : 'outlined'}
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setActivityDays(30); setPage(1); setAllOffers([]);
                                                const params = new URLSearchParams(searchParams);
                                                params.set('days', '30');
                                                setSearchParams(params, { replace: true });
                                            }}
                                        >
                                            30 {t('days')}
                                        </SparaButton>
                                        <SparaButton
                                            variant={activityDays === 60 ? 'filled' : 'outlined'}
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setActivityDays(60); setPage(1); setAllOffers([]);
                                                const params = new URLSearchParams(searchParams);
                                                params.set('days', '60');
                                                setSearchParams(params, { replace: true });
                                            }}
                                        >
                                            60 {t('days')}
                                        </SparaButton>
                                        <SparaButton
                                            variant={activityDays === 90 ? 'filled' : 'outlined'}
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setActivityDays(90); setPage(1); setAllOffers([]);
                                                const params = new URLSearchParams(searchParams);
                                                params.set('days', '90');
                                                setSearchParams(params, { replace: true });
                                            }}
                                        >
                                            90 {t('days')}
                                        </SparaButton>
                                        <SparaButton
                                            variant={activityDays === null ? 'filled' : 'outlined'}
                                            color="secondary"
                                            size="small"
                                            onClick={() => {
                                                setActivityDays(null); setPage(1); setAllOffers([]);
                                                const params = new URLSearchParams(searchParams);
                                                params.delete('days');
                                                setSearchParams(params, { replace: true });
                                            }}
                                        >
                                            {t('all')}
                                        </SparaButton>
                                    </>
                                )}
                            </Box>
                            <Box style={{ display: 'flex', gap: 8 }}>
                                <SparaButton
                                    variant={viewMode === 'card' ? 'filled' : 'outlined'}
                                    color="primary"
                                    onClick={() => {
                                        setViewMode('card');
                                        localStorage.setItem('offersViewMode', 'card');
                                    }}
                                >
                                    <ViewModule />
                                </SparaButton>
                                <SparaButton
                                    variant={viewMode === 'table' ? 'filled' : 'outlined'}
                                    color="primary"
                                    onClick={() => {
                                        setViewMode('table');
                                        localStorage.setItem('offersViewMode', 'table');
                                    }}
                                >
                                    <ViewList />
                                </SparaButton>
                            </Box>
                        </Box>
                        <SparaFilter
                            filters={filters}
                            onFilterClick={handleFilterClick}
                            onClearAll={handleClearAll}
                            search={search}
                            onSearchChange={handleSearchChange}
                            t={t}
                            showSearch={true}
                            loading={isValidating}
                            filterGroups={[
                                [
                                    {
                                        id: 'display',
                                        label: 'offers.in_display',
                                        value: 'true',
                                        color: '#078f5c',
                                    },
                                    {
                                        id: 'display',
                                        label: 'offers.not_in_display',
                                        value: 'false',
                                        color: '#d32f2f',
                                    },
                                ],
                                getSpecialCompanyOptions(),
                            ]}
                        />
                        {offersData && (
                            <div
                                className="offers-count-text"
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    fontStyle: 'italic',
                                    color: '#007aaa',
                                    marginTop: '8px',
                                    marginLeft: '8px',
                                }}
                            >
                                {`${t('filter.showing')} ${allOffers.length} ${t(
                                    'filter.of'
                                )} ${totalCount} tilboðum`}
                            </div>
                        )}

                        {viewMode === 'table' ? (
                            <OfferTable
                                data={filteredOffers}
                                dataGroup={specialCompanies.results}
                                sort={sort}
                                updateButton={canUpdate ? updateButton : undefined}
                                reportButton={canViewDetails ? reportButton : undefined}
                                deleteButton={canDelete ? deleteButton : undefined}
                                changeOfferActive={changeOfferActive}
                                notificationButton={canNotify ? notificationButton : undefined}
                                canManageOffer={canManageOffer}
                                onDuplicate={canCreate ? duplicateOffer : undefined}
                            />
                        ) : (
                            <OfferCardView
                                data={allOffers}
                                dataGroup={specialCompanies.results}
                                sort={sort}
                                loading={(!offersData && !offersError) || (isValidating && allOffers.length === 0)}
                                updateButton={canUpdate ? updateButton : undefined}
                                reportButton={canViewDetails ? reportButton : undefined}
                                deleteButton={canDelete ? deleteButton : undefined}
                                changeOfferActive={changeOfferActive}
                                notificationButton={canNotify ? notificationButton : undefined}
                                canManageOffer={canManageOffer}
                                onDuplicate={canCreate ? duplicateOffer : undefined}
                                pageIndex={cardPageIndex}
                                pageSize={cardPageSize}
                                pageCount={Math.ceil(allOffers.length / cardPageSize)}
                                canPreviousPage={cardPageIndex > 0}
                                canNextPage={
                                    cardPageIndex < Math.ceil(allOffers.length / cardPageSize) - 1
                                }
                                onChangePage={(page) => setCardPageIndex(page)}
                                onChangePageSize={(size) => {
                                    setCardPageSize(parseInt(size));
                                    setCardPageIndex(0);
                                }}
                            />
                        )}

                        {/* Shimmer for load more */}
                        {isValidating &&
                            page > 1 &&
                            (viewMode === 'card' ? (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(170px, 1fr))',
                                        gap: 16,
                                        padding: '8px 0',
                                    }}
                                >
                                    {[...Array(4)].map((_, i) => (
                                        <OfferCardSkeleton key={i} />
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    {[...Array(5)].map((_, i) => (
                                        <OfferTableRowSkeleton key={i} />
                                    ))}
                                </div>
                            ))}

                        {/* Shimmer for initial load */}
                        {isValidating &&
                            page === 1 &&
                            allOffers.length === 0 &&
                            (viewMode === 'card' ? (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(170px, 1fr))',
                                        gap: 16,
                                        padding: '8px 0',
                                    }}
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <OfferCardSkeleton key={i} />
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    {[...Array(10)].map((_, i) => (
                                        <OfferTableRowSkeleton key={i} />
                                    ))}
                                </div>
                            ))}

                        {/* No results message - only show when not loading and no data */}
                        {!isValidating && allOffers.length === 0 && !offersData && (
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '32px',
                                    color: 'rgba(0,0,0,0.5)',
                                    fontSize: '16px',
                                }}
                            >
                                {t('no_results_with_current_filter')}
                            </div>
                        )}

                        {/* Intersection observer target */}
                        {isActiveLoadMore && allOffers.length < totalCount && (
                            <div ref={loadMoreRef} style={{}}></div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
            <NotificationModal
                open={notificationModal.open}
                offer={notificationModal.offer}
                t={t}
                isSuperAdmin={isSuperAdmin}
                onClose={() => setNotificationModal({ open: false, offer: null })}
                onTotalCountChange={(count) => setNotificationTotalCount(count)}
                onSend={async (options = {}) => {
                    const includeUnregistered =
                        typeof options === 'boolean'
                            ? options
                            : options.includeUnregistered || false;
                    const groupIds =
                        typeof options === 'boolean' ? [] : options.groupIds || [];
                    const offerId = notificationModal.offer?.id;
                    setNotificationModal({ open: false, offer: null });
                    setNotificationProgress({
                        open: true,
                        progress: 0,
                        sentCount: 0,
                        totalCount: notificationTotalCount,
                    });
                    setNotificationBatchIds([]);
                    try {
                        const body = {
                            include_unregistered: includeUnregistered,
                        };
                        if (groupIds.length) {
                            body.group_ids = groupIds.join(',');
                        }
                        const res = await fetcher(
                            offerApi.notificationSend(offerId),
                            'POST',
                            body
                        );
                        // Ná í öll batchIds úr svari
                        let batchIds = [];
                        if (res?.results?.batch_ids) batchIds = res.results.batch_ids;
                        else if (res?.results?.batch_id) batchIds = [res.results.batch_id];
                        else if (res?.batch_id) batchIds = [res.batch_id];
                        if (batchIds.length) {
                            setNotificationBatchIds(batchIds);
                        } else {
                            setNotificationProgress({
                                open: false,
                                progress: 100,
                                sentCount: 0,
                                totalCount: notificationTotalCount,
                            });
                            // Uppfæra SWR cache eftir notification
                            mutateOffers();
                            setDialog({
                                open: true,
                                title: t('notification_sent'),
                                message: t('notification_was_sent', {
                                    count: notificationTotalCount,
                                }),
                                type: 'success',
                                onConfirm: () => setDialog(DIALOG_CLOSED),
                                onCancel: null,
                                confirmText: t('ok'),
                            });
                        }
                    } catch (e) {
                        setNotificationProgress({
                            open: false,
                            progress: 0,
                            sentCount: 0,
                            totalCount: notificationTotalCount,
                        });
                        // Uppfæra SWR cache þótt villa komi upp
                        mutateOffers();
                        setDialog({
                            open: true,
                            title: t('error'),
                            message:
                                t('error_sending_notification') ||
                                'Villa kom upp við að senda tilkynningu.',
                            type: 'danger',
                            onConfirm: () => setDialog(DIALOG_CLOSED),
                            onCancel: null,
                            confirmText: t('ok'),
                        });
                    }
                }}
            />
            <NotificationProgressModal
                open={notificationProgress.open}
                progress={notificationProgress.progress}
                t={t}
                sentCount={notificationProgress.sentCount}
                totalCount={notificationTotalCount}
            />
        </GridContainer>
    );
};

export default withTranslation('', { withRef: true })(Offers);
